import { createFileRoute, Navigate } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import {
  runProductsSearchSchema,
  type RunProductsSearch,
} from '~/features/catalog-enrichment/utils/run-products-search';

const RunProductsPage = lazy(() => import('~/features/catalog-enrichment/pages/run-products-page'));
const RunBatchProductsPage = lazy(
  () => import('~/features/catalog-enrichment/pages/run-batch-products-page'),
);

/** Old step drills (`?stage=&status=`) as the new Products filter's outcome. */
const STATUS_TO_OUTCOME: Partial<Record<string, string>> = {
  FAILED: 'FAILED',
  SKIPPED: 'DROPPED',
  REVISIT: 'HELD_BACK',
  PENDING: 'IN_FLIGHT',
};

export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-enrichment/runs/$runId/products/',
)({
  validateSearch: runProductsSearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Filter/page changes replace the history entry so Back leaves the screen, not the filter.
  const onSearchChange = (patch: Partial<RunProductsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  // Decisions moved to the Review tab: an old "Products?decision=…" link lands there.
  if (search.decision) {
    return (
      <Navigate
        to="/catalog-enrichment/runs/$runId/gate"
        params={{ runId }}
        search={search}
        replace
      />
    );
  }

  // An old step drill lands on the matching Products filter.
  const drillOutcome = search.status ? STATUS_TO_OUTCOME[search.status] : undefined;
  if (search.stage && drillOutcome && !search.view) {
    return (
      <Navigate
        to="/catalog-enrichment/runs/$runId/products"
        params={{ runId }}
        search={{ filter: `${search.stage}:${drillOutcome}` }}
        replace
      />
    );
  }

  // The listing grid, the skipped list and the review lens are still the listing-based page; the
  // default view is the product table over the funnel matrix (guide part 2 §4.3).
  const legacy =
    !!search.view ||
    !!search.lens ||
    !!search.listing ||
    !!search.listingIds ||
    !!search.feedProductId;
  const Page = legacy ? RunProductsPage : RunBatchProductsPage;
  return (
    <Suspense fallback={<PageSkeleton />}>
      {/* Keyed by batch so switching batches never carries one batch's state into the next. */}
      <Page key={runId} runId={runId} search={search} onSearchChange={onSearchChange} />
    </Suspense>
  );
}
